import type { UIEvent } from 'react'
import React from 'react'
import { ListItem, useOverlay, InlineButton, useInfoSheet } from '@fs/zion-ui'
import { useHelpOverlay } from '@fs/zion-help-overlay'
import { useTranslation } from 'react-i18next'
import {
  PersonDuplicates,
  PersonSearch,
  PersonFamily,
  ContentFlag,
  ContentDelete,
  ContentPrint,
  MenuSettings,
  ContentAttached,
  HelpAnnouncement,
} from '@fs/zion-icon'
import { useParams } from '@fs/zion-router'
import { getFSTreesUrl } from '@fs/zion-search-partners'
import { trackEvent } from '@fs/zion-analytics'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useFeatureFlag } from '@fs/zion-flags'
import { useResearchHelpContext } from '../../contexts/ResearchHelpContext'
import { useFocusFamilyMembersContext } from '../../contexts/FocusFamilyMembersContext'
import SettingsInfoSheet from '../settings-info-sheet/SettingsInfoSheet'
import useDeleteConstraint from '../../../data-service/useDeleteConstraint'
import useFocusPerson from '../../../data-service/useFocusPerson'
import SourcesInfoSheet from '../info-sheets/SourcesInfoSheet'
import PossibleDuplicatesNotAvailableDialog from './PossibleDuplicatesNotAvailableDialog'

const ReportAbuseOverlay = React.lazy(() => import('@fs/zion-report-abuse-overlay'))
const MergeByIdDialog = React.lazy(() => import('./MergeByIdDialog'))
const DeletePersonDialog = React.lazy(() => import('./DeletePersonDialog'))
const PrintDialog = React.lazy(() => import('./PrintDialog'))

type Props = {
  hideIcons?: boolean
  showLayoutSettings?: boolean
}

// These items are exported without being wrapped in a List so they can be reused in the MobileMenu component
export default function ToolsMenuItems({ hideIcons = false, showLayoutSettings }: Props): JSX.Element | null {
  const [t] = useTranslation()
  const { pid } = useParams()
  const { focusPerson } = useFocusPerson()
  const cetPossibleDuplicatesIsOn = useFeatureFlag('treeWeb_cet_possibleDuplicates').isOn

  const mergeByIdOverlay = useOverlay()
  const deletePersonOverlay = useOverlay()
  const reportAbuseOverlay = useOverlay()
  const possibleDuplicatesNotAvailableOverlay = useOverlay()
  const printOverlay = useOverlay()
  const settingsInfoSheet = useInfoSheet()
  const sourcesInfoSheet = useInfoSheet()

  const { deleteConstraint } = useDeleteConstraint(pid)
  const canDelete = deleteConstraint && deleteConstraint.personDeleteConstraint === 'CAN_DELETE_PERSON'

  const { focusFamilyMembers } = useFocusFamilyMembersContext()
  const { researchHelpData } = useResearchHelpContext()
  const { isResearcherGroup } = useGroupsContext() ?? {}

  const helpActionUrl = 'https://www.familysearch.org/help/helpcenter/article/family-tree-whats-new'
  const helpOverlay = useHelpOverlay(helpActionUrl)
  const deletePersonUrl =
    'https://www.familysearch.org/help/helpcenter/article/why-cant-i-delete-this-person-in-family-tree'
  const deletePersonHelpOverlay = useHelpOverlay(deletePersonUrl)

  const possibleDuplicatesCount = researchHelpData.duplicatePersonDataProblems?.length ?? 0

  if (!focusPerson || !focusFamilyMembers) return null
  return (
    <>
      {showLayoutSettings && (
        <ListItem
          data-testid="tool-mylayoutsettings"
          dense
          linkName="Tools: My Layout Settings"
          onClick={settingsInfoSheet.handleClick}
          startElement={hideIcons ? undefined : <ListItem.Icon Icon={MenuSettings} />}
          primaryText={t('tree-person.layout-settings.label')}
        />
      )}

      <ListItem
        data-testid="tool-sourcessheet"
        dense
        linkName="Tools: Sources Sheet"
        onClick={sourcesInfoSheet.handleClick}
        startElement={hideIcons ? undefined : <ListItem.Icon Icon={ContentAttached} />}
        primaryText={`${t('tree-person.sources.header')} (${focusPerson?.sourceCount})`}
      />

      <ListItem
        data-testid="tool-whats-new-dialog"
        dense
        linkName="Tools: What's New Dialog"
        onClick={helpOverlay.handleClick}
        startElement={hideIcons ? undefined : <ListItem.Icon Icon={HelpAnnouncement} />}
        primaryText={t('tree-person.whats-new.heading')}
      />

      {!focusPerson.readOnly && (
        <ListItem
          data-testid="tool-possibleduplicates"
          dense
          disabled={isResearcherGroup && !cetPossibleDuplicatesIsOn}
          linkName="Tools: Possible Duplicates"
          onClick={
            !focusPerson.visibleToAllWhenUsingFamilySearchApps
              ? possibleDuplicatesNotAvailableOverlay.handleClick
              : undefined
          }
          to={focusPerson.visibleToAllWhenUsingFamilySearchApps ? `/research-help/${pid}` : undefined}
          startElement={!hideIcons ? <ListItem.Icon Icon={PersonDuplicates} /> : undefined}
          primaryText={`${t('tree-person.possible-duplicates.label')} (${possibleDuplicatesCount})`}
        />
      )}

      <ListItem
        data-testid="tool-findsimilarpeople"
        dense
        disabled={isResearcherGroup}
        linkName="Tools: Find Similar People"
        to={getFSTreesUrl({ person: focusPerson, familyMembers: focusFamilyMembers })}
        external
        target="_blank"
        startElement={!hideIcons ? <ListItem.Icon Icon={PersonSearch} /> : undefined}
        primaryText={t('tree-person.find-similar-people.label')}
      />

      <ListItem
        data-testid="tool-mergebyid"
        dense
        onClick={(e) => {
          trackEvent({ link_name: 'Tools: Merge By ID' })
          mergeByIdOverlay.handleClick(e)
        }}
        startElement={!hideIcons ? <ListItem.Icon Icon={PersonFamily} /> : undefined}
        primaryText={t('tree-person.merge-by-id.label')}
        disabled={focusPerson.readOnly}
      />

      <ListItem
        data-testid="tool-reportabuse"
        dense
        onClick={(event) => {
          trackEvent({ link_name: 'Tools: Report Abuse' })
          reportAbuseOverlay.handleClick(event)
        }}
        startElement={!hideIcons ? <ListItem.Icon Icon={ContentFlag} /> : undefined}
        primaryText={t('tree-person.report-abuse.label')}
      />

      <ListItem
        data-testid="tool-deleteperson"
        dense
        disabled={!canDelete}
        onClick={(event) => {
          trackEvent({ link_name: 'Tools: Delete Person' })
          deletePersonOverlay.handleClick(event)
        }}
        startElement={!hideIcons ? <ListItem.Icon Icon={ContentDelete} /> : undefined}
        primaryText={t('tree-person.delete-person.label')}
        secondaryText={
          canDelete ? undefined : (
            <InlineButton data-testid="why-no-can-delete-button" onClick={deletePersonHelpOverlay.handleClick}>
              {t('tree-person.delete-person-unavailable.header')}
            </InlineButton>
          )
        }
      />

      <ListItem
        data-testid="tool-print"
        dense
        onClick={(event: UIEvent) => {
          trackEvent({ link_name: 'Tools: Print' })
          printOverlay.handleClick(event)
        }}
        startElement={!hideIcons ? <ListItem.Icon Icon={ContentPrint} /> : undefined}
        primaryText={t('tree-person.print-options.label')}
      />

      <React.Suspense fallback="">
        {(mergeByIdOverlay.isOpen || mergeByIdOverlay.transitioning) && <MergeByIdDialog overlay={mergeByIdOverlay} />}
        {(deletePersonOverlay.isOpen || deletePersonOverlay.transitioning) && (
          <DeletePersonDialog overlay={deletePersonOverlay} />
        )}
        {(possibleDuplicatesNotAvailableOverlay.isOpen || possibleDuplicatesNotAvailableOverlay.transitioning) && (
          <PossibleDuplicatesNotAvailableDialog overlay={possibleDuplicatesNotAvailableOverlay} />
        )}
        {(reportAbuseOverlay.isOpen || reportAbuseOverlay.transitioning) && (
          <ReportAbuseOverlay
            overlay={reportAbuseOverlay}
            subject={`Person Details: ${focusPerson?.name} | ${pid}`}
            additionalInfo={`
      Person ID: ${pid}
      Person Name: ${focusPerson?.name}
      URL: ${window.location.href}
      `}
          />
        )}
        {(printOverlay.isOpen || printOverlay.transitioning) && <PrintDialog overlay={printOverlay} />}
      </React.Suspense>
      {showLayoutSettings && <SettingsInfoSheet infoSheetConfig={settingsInfoSheet} />}
      <SourcesInfoSheet infoSheet={sourcesInfoSheet} />
    </>
  )
}
